import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { MessageSquare, Send, Trash2, AtSign } from 'lucide-react'
import { formatMyanmarDateTime } from '@/lib/dateUtils'

interface Comment {
  id: number
  entity_type: string
  entity_id: number
  user_id: number
  user_name: string
  content: string
  mentions: number[]
  created_at: string
}

interface SimpleUser {
  id: number
  full_name: string
  role: string
}

function timeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return formatMyanmarDateTime(dateStr)
}

function renderContent(content: string) {
  const parts = content.split(/(@[A-Za-z][A-Za-z\s]*?)(?=\s@|\s*$|[.,!?;])/g)
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return <span key={i} className="text-red-800 font-medium bg-red-50 px-0.5 rounded">{part}</span>
    }
    return <span key={i}>{part}</span>
  })
}

export default function CommentsSection({ entityType, entityId }: { entityType: string; entityId: number }) {
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const [showMentions, setShowMentions] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [cursorPos, setCursorPos] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { data: comments, isLoading } = useQuery<Comment[]>({
    queryKey: ['comments', entityType, entityId],
    queryFn: () => api.get('/comments', { params: { entity_type: entityType, entity_id: entityId } }).then(r => r.data),
  })

  const { data: users } = useQuery<SimpleUser[]>({
    queryKey: ['users-list'],
    queryFn: () => api.get('/users/list').then(r => r.data),
  })

  const postComment = useMutation({
    mutationFn: (data: any) => api.post('/comments', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', entityType, entityId] })
      setText('')
    },
  })

  const deleteComment = useMutation({
    mutationFn: (id: number) => api.delete(`/comments/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comments', entityType, entityId] }),
  })

  const handleSubmit = () => {
    if (!text.trim()) return
    postComment.mutate({ entity_type: entityType, entity_id: entityId, content: text.trim() })
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    const pos = e.target.selectionStart || 0
    setText(val)
    setCursorPos(pos)
    const beforeCursor = val.substring(0, pos)
    const atMatch = beforeCursor.match(/@([A-Za-z\s]*)$/)
    if (atMatch) {
      setMentionQuery(atMatch[1].toLowerCase())
      setShowMentions(true)
    } else {
      setShowMentions(false)
    }
  }

  const insertMention = (name: string) => {
    const beforeCursor = text.substring(0, cursorPos)
    const afterCursor = text.substring(cursorPos)
    const atIdx = beforeCursor.lastIndexOf('@')
    const newText = beforeCursor.substring(0, atIdx) + `@${name} ` + afterCursor
    setText(newText)
    setShowMentions(false)
    textareaRef.current?.focus()
  }

  const filteredUsers = (users || []).filter(u => u.full_name.toLowerCase().includes(mentionQuery))

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')

  return (
    <div className="space-y-3 pt-3 border-t border-slate-200">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
        <MessageSquare size={13} /> Comments ({comments?.length || 0})
      </div>

      <div className="relative">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit() } }}
          placeholder="Write a comment... Use @ to mention someone"
          rows={2}
          className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 pr-10 resize-none"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!text.trim() || postComment.isPending}
          className="absolute right-2 bottom-2 p-1.5 text-red-800 hover:text-red-900 disabled:text-slate-300"
        >
          <Send size={16} />
        </button>
        {showMentions && filteredUsers.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mb-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-40 overflow-y-auto z-20">
            <div className="px-3 py-1 text-xs text-slate-400 border-b flex items-center gap-1"><AtSign size={10} /> Mention a person</div>
            {filteredUsers.map(u => (
              <button key={u.id} type="button" onClick={() => insertMention(u.full_name)}
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-red-50 flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-red-100 text-red-900 flex items-center justify-center text-xs font-medium">
                  {u.full_name.charAt(0)}
                </div>
                <span>{u.full_name}</span>
                <span className="text-xs text-slate-400 ml-auto capitalize">{u.role}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading && <div className="text-sm text-slate-400">Loading comments...</div>}

      <div className="space-y-2">
        {(comments || []).map(c => (
          <div key={c.id} className="bg-slate-50 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-full bg-red-100 text-red-900 flex items-center justify-center text-xs font-bold shrink-0">
                {c.user_name.charAt(0)}
              </div>
              <span className="text-sm font-medium text-slate-900">{c.user_name}</span>
              <span className="text-xs text-slate-400">{timeAgo(c.created_at)}</span>
              {(c.user_id === currentUser.id || currentUser.role === 'admin' || currentUser.role === 'super_admin') && (
                <button onClick={() => deleteComment.mutate(c.id)} className="ml-auto text-slate-300 hover:text-red-500">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            <div className="text-sm text-slate-700 pl-8">{renderContent(c.content)}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
